// FILE: NewThreadDialog.logic.ts
// Purpose: Pure helpers behind the "New thread" dialog — defaults, slugging,
//          and which fields apply to the current selection.
// Layer: Web component logic
// Exports: resolveNewThreadDialogDefaults, slugifyThreadName, resolveWorktreeBranchPreview,
//          setupScriptForProject, isNameFieldApplicable, resolveIssueThreadName,
//          shouldReplaceThreadNameWithIssue, projectHasPlaneBoard,
//          resolvePullRequestThreadName, isEnvModeLockedByPullRequest,
//          resolveDefaultEnvironmentId, environmentOptionLabel,
//          qaOperationCopy, qaOperationThreadName, resolveQaAgentId,
//          qaAgentModelSelection.

import { sanitizeBranchFragment, WORKTREE_BRANCH_PREFIX } from "@taskin-code/shared/git";
import type {
  EnvironmentId,
  ManagedAgentId,
  ManagedAgentMode,
  ModelSelection,
  ProjectId,
  ProjectScript,
  ProviderKind,
  QaOperationKind,
} from "@taskin-code/contracts";
import { filterQaCapableAgents } from "@taskin-code/shared/managedAgentQaCapability";

import type { DraftThreadEnvMode } from "../composerDraftStore";
import { setupProjectScript } from "@taskin-code/shared/projectScripts";

/** The slice of the store's `Project` this dialog reads. */
export interface NewThreadDialogProject {
  id: ProjectId;
  name: string;
  cwd: string;
  iconFileName: string | null;
  color: string | null;
  isBareRepository?: boolean | undefined;
  scripts?: ProjectScript[] | undefined;
  defaultModelSelection?: ModelSelection | null | undefined;
  planeProjectId?: string | null | undefined;
  planeProjectIdentifier?: string | null | undefined;
}

/** Does this project have a Plane board to pick an issue from? */
export function projectHasPlaneBoard(project: NewThreadDialogProject | null): boolean {
  return Boolean(project?.planeProjectId);
}

export interface NewThreadDialogDefaults {
  projectId: ProjectId | null;
  envMode: DraftThreadEnvMode;
  name: string;
  runSetupScript: boolean;
}

/**
 * A bare repository has no working copy to run a thread in, so `local` is not
 * an option there regardless of the user's default.
 */
export function resolveEnvModeForProject(
  project: NewThreadDialogProject | null,
  preferred: DraftThreadEnvMode,
): DraftThreadEnvMode {
  if (project?.isBareRepository === true) {
    return "worktree";
  }
  return preferred;
}

export function resolveNewThreadDialogDefaults(input: {
  projects: ReadonlyArray<NewThreadDialogProject>;
  preferredProjectId: ProjectId | null;
  defaultEnvMode: DraftThreadEnvMode;
}): NewThreadDialogDefaults {
  const project =
    input.projects.find((candidate) => candidate.id === input.preferredProjectId) ??
    input.projects[0] ??
    null;
  return {
    projectId: project?.id ?? null,
    envMode: resolveEnvModeForProject(project, input.defaultEnvMode),
    name: "",
    // Setup actions are opt-out: a project that declares one declared it
    // because that is how its worktrees are meant to start.
    runSetupScript: true,
  };
}

/**
 * Slug shown live under the name input. Deliberately the same sanitizer the
 * server applies, so what the user reads is what the branch will be called.
 */
export function slugifyThreadName(raw: string): string {
  if (raw.trim().length === 0) {
    return "";
  }
  return sanitizeBranchFragment(raw);
}

/**
 * The branch the thread will end up on, for display. `null` means "let the
 * server pick a throwaway name", which also leaves the first-turn LLM rename
 * free to give it a semantic one later.
 */
export function resolveWorktreeBranchPreview(name: string): string | null {
  const slug = slugifyThreadName(name);
  return slug.length > 0 ? `${WORKTREE_BRANCH_PREFIX}/${slug}` : null;
}

/** The single `runOnWorktreeCreate` action of a project, if it has one. */
export function setupScriptForProject(
  project: NewThreadDialogProject | null,
): ProjectScript | null {
  if (!project?.scripts || project.scripts.length === 0) {
    return null;
  }
  return setupProjectScript(project.scripts);
}

/**
 * Name and setup action only mean something when a worktree is created; a
 * `local` thread runs straight in the project checkout.
 */
export function isWorktreeFieldApplicable(envMode: DraftThreadEnvMode): boolean {
  return envMode === "worktree";
}

/**
 * What the Name field autofills to when an issue is picked. Slugged and
 * truncated the same way any other name is (see `resolveWorktreeBranchPreview`),
 * so the field just shows the readable form.
 */
export function resolveIssueThreadName(ref: string, title: string): string {
  return `${ref}: ${title}`;
}

/**
 * The pull request a thread is being opened for.
 *
 * Four fields and not the `PullRequestListEntry` they come from: the branch is what the worktree
 * is cut from, the number and project are the link that outlives this dialog, and the title only
 * names the thread. Everything else on that entry — state, mergeability, the counts — is true at
 * the moment it was read and would be stale by the time anyone looked at it here.
 */
export interface NewThreadDialogPullRequest {
  projectId: ProjectId;
  number: number;
  headBranch: string;
  title: string;
}

/**
 * What the Name field autofills to when a pull request is picked. Mirrors `resolveIssueThreadName`
 * — the `#10` reads as the reference, and the title says what the work is.
 */
export function resolvePullRequestThreadName(number: number, title: string): string {
  return `#${number}: ${title}`;
}

/**
 * Whether the environment selector can still be used.
 *
 * A pull request pins it to `worktree`. Opening one means checking its head branch out somewhere,
 * and `local` is the project's own checkout — the one the person may have work sitting in. The
 * selector is disabled rather than hidden so the reason can be shown next to it; picking the pull
 * request is what moved it, and that has to be visible.
 */
export function isEnvModeLockedByPullRequest(
  pullRequest: NewThreadDialogPullRequest | null,
): boolean {
  return pullRequest !== null;
}

/**
 * Whether picking an issue should overwrite the current Name field.
 *
 * Only when the field is empty, or still holds exactly the autofill from a
 * previous issue pick — never when the person has typed their own name. Without
 * the second condition, picking issue B after issue A would leave A's name in
 * place; without the first, every keystroke the person makes would survive a
 * later issue pick, which is also correct but for a different reason.
 */
export function shouldReplaceThreadNameWithIssue(
  currentName: string,
  previousIssueAutofill: string | null,
): boolean {
  return currentName.trim().length === 0 || currentName === previousIssueAutofill;
}

/**
 * The same question for a pull request pick, and deliberately the same answer: a name the person
 * typed is never overwritten, and an autofill from a previous pick is. Kept as its own name
 * because the two picks track their previous autofill separately — choosing an issue after a pull
 * request must not treat the pull request's name as "typed by hand".
 */
export const shouldReplaceThreadNameWithPullRequest = shouldReplaceThreadNameWithIssue;

/** The slice of an environment row this dialog needs to offer it. */
export interface NewThreadDialogEnvironment {
  readonly id: EnvironmentId;
  readonly kind: "personal" | "project";
  readonly name: string | null;
  readonly status: "provisioning" | "ready" | "failed";
}

/**
 * Which environment a new thread opens in when nobody picks.
 *
 * This is the old silent auto-assignment, kept — but as the *default of a
 * visible control* rather than a decision made behind the person's back. The
 * order is the server's (`environmentResolution`), and it has to stay that way:
 * a dialog that pre-selected `qa` for somebody who owns a personal environment
 * would offer to move their week of test data to the team's, with one Enter.
 *
 * `preferred` wins over everything: it is «Abrir hilo aquí» on a card, which is
 * a person pointing at an environment.
 */
export function resolveDefaultEnvironmentId(input: {
  readonly environments: ReadonlyArray<NewThreadDialogEnvironment>;
  readonly preferred: EnvironmentId | null;
}): EnvironmentId | null {
  const ready = input.environments.filter((environment) => environment.status === "ready");
  if (input.preferred !== null) {
    const pointed = ready.find((environment) => environment.id === input.preferred);
    if (pointed !== undefined) return pointed.id;
  }
  const personal = ready.find((environment) => environment.kind === "personal");
  if (personal !== undefined) return personal.id;
  const qa = ready.find(
    (environment) => environment.kind === "project" && environment.name === "qa",
  );
  if (qa !== undefined) return qa.id;
  return ready.find((environment) => environment.kind === "project")?.id ?? null;
}

/** How an environment reads in the selector. */
export function environmentOptionLabel(environment: NewThreadDialogEnvironment): string {
  if (environment.kind === "personal") return "Mi entorno personal";
  return `Entorno del proyecto: ${environment.name ?? "sin nombre"}`;
}

/* ------------------------------------------------------------------------ *
 * Environment operations («operación de entorno»)
 *
 * The card's buttons do not sync, reset or copy anything: they open this
 * dialog with the project, the environment and the message already decided,
 * and the person presses Send in the composer. So in this mode the dialog has
 * one real question left — which agent — and it may only offer agents that can
 * actually carry the operation out.
 * ------------------------------------------------------------------------ */

/** What the dialog calls itself while it is opening an operation thread. */
export interface QaOperationCopy {
  readonly title: string;
  readonly description: string;
  readonly confirmLabel: string;
  /** Prefix of the thread's title, before the environment's name. */
  readonly threadNamePrefix: string;
}

export function qaOperationCopy(kind: QaOperationKind): QaOperationCopy {
  switch (kind) {
    case "sync":
      return {
        title: "Actualizar el entorno",
        description:
          "Se abre un hilo con el mensaje ya escrito. Nada se sincroniza hasta que lo envíes.",
        confirmLabel: "Abrir hilo de actualización",
        threadNamePrefix: "Actualizar",
      };
    case "reset-data":
      return {
        title: "Resetear los datos del entorno",
        description:
          "Se abre un hilo con el mensaje ya escrito. Los datos no se tocan hasta que lo envíes.",
        confirmLabel: "Abrir hilo de reseteo",
        threadNamePrefix: "Resetear datos de",
      };
    case "clone":
      return {
        title: "Copiar datos entre entornos",
        description:
          "Se abre un hilo con el mensaje ya escrito. La copia no empieza hasta que lo envíes.",
        confirmLabel: "Abrir hilo de copia",
        threadNamePrefix: "Copiar datos a",
      };
    // The two halves of a trial. Their wording insists on the same thing the
    // other three do — nothing happens until Send — and adds the fact that
    // only this pair has: the environment is shared, so putting a pull request
    // on it changes what everybody else is looking at.
    case "try-pr":
      return {
        title: "Probar el PR sobre el entorno",
        description:
          "Se abre un hilo con el mensaje ya escrito. El PR no se monta hasta que lo envíes, y " +
          "mientras esté montado el entorno lo verá así todo el mundo.",
        confirmLabel: "Abrir hilo de prueba",
        threadNamePrefix: "Probar PR en",
      };
    case "back-to-base":
      return {
        title: "Volver a la rama base",
        description:
          "Se abre un hilo con el mensaje ya escrito. El PR sigue montado hasta que lo envíes; " +
          "al hacerlo vuelven el código y los datos de antes de probarlo.",
        confirmLabel: "Abrir hilo de vuelta a base",
        threadNamePrefix: "Volver a base",
      };
    // The isolated pair. What their wording has to carry, and the trial pair's
    // does not, is that the shared environment is NOT involved: that is the
    // whole reason to press this button instead of the other one.
    case "preview-create":
      return {
        title: "Montar una preview aislada del PR",
        description:
          "Se abre un hilo con el mensaje ya escrito. La preview no se monta hasta que lo " +
          "envíes, y cuando lo haga tendrá su propia URL: el entorno compartido no se toca.",
        confirmLabel: "Abrir hilo de preview",
        threadNamePrefix: "Preview del PR en",
      };
    case "preview-destroy":
      return {
        title: "Destruir la preview del PR",
        description:
          "Se abre un hilo con el mensaje ya escrito. La preview sigue en pie hasta que lo " +
          "envíes; al hacerlo se borran su base de datos y su worktree, y su URL deja de responder.",
        confirmLabel: "Abrir hilo de destrucción",
        threadNamePrefix: "Destruir preview en",
      };
  }
}

/**
 * The thread's title. Written here rather than left to the first-turn rename so
 * that the thread is identifiable in the sidebar the moment it appears — which
 * is what the card links to while the operation runs.
 */
export function qaOperationThreadName(kind: QaOperationKind, environmentName: string): string {
  return `${qaOperationCopy(kind).threadNamePrefix} ${environmentName}`;
}

/** The slice of a managed agent this dialog needs to offer it for an operation. */
export interface NewThreadDialogAgent {
  readonly id: ManagedAgentId;
  readonly name: string;
  readonly provider: ProviderKind;
  readonly model: string;
  readonly mode: ManagedAgentMode;
  readonly tools: ReadonlyArray<string>;
}

/**
 * Which agent the operation is handed to.
 *
 * A choice the person already made survives as long as it is still capable —
 * the list arrives asynchronously, and a late arrival must not overrule them.
 * Otherwise the first capable agent, in the catalogue's own order: this dialog's
 * contract is that Enter alone works, and an operation thread does nothing on
 * its own anyway. Creating it only writes a message into a composer that a
 * person still has to read and send, so a preselection that is not the one they
 * wanted costs a click on a select, not a synchronised environment.
 */
export function resolveQaAgentId(input: {
  readonly agents: ReadonlyArray<NewThreadDialogAgent>;
  readonly current: ManagedAgentId | null;
  /**
   * Narrows the list to the agents that can run *this* operation. The server
   * applies the same rule to the command, so an operation left out here would
   * be a menu offering an agent the route then refuses.
   */
  readonly operation?: QaOperationKind | null;
}): ManagedAgentId | null {
  const capable = filterQaCapableAgents(
    input.agents,
    ...(input.operation == null ? [] : ([input.operation] as const)),
  );
  if (input.current !== null && capable.some((agent) => agent.id === input.current)) {
    return input.current;
  }
  return capable[0]?.id ?? null;
}

/**
 * The model selection an operation thread starts with: the agent's own provider
 * and model, never the person's default. An agent's tools are declared against
 * the provider it was configured for, and starting it somewhere else is how a
 * thread ends up with an agent whose QA tools are not mounted.
 */
export function qaAgentModelSelection(agent: NewThreadDialogAgent): ModelSelection {
  return {
    provider: agent.provider,
    model: agent.model,
    agentId: agent.id,
  } as ModelSelection;
}
